import React from "react";
import { Box, Typography } from "@mui/material";
import { Card, GroupedBarChart } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "components/common/CustomSkeleton";
import { formatCash, formatCashFull } from "utils/formatCash";
import { WeeklyBucket } from "utils/spendBuckets";

export interface SpendComparisonChartProps {
  weekly: WeeklyBucket[];
  currency: string;
  loading?: boolean;
}

/**
 * The trailing four weeks against the four before them.
 *
 * Weeks are counted back from today rather than snapped to calendar weeks, so
 * week 4 always ends today and the two blocks line up.
 */
const SpendComparisonChart: React.FC<SpendComparisonChartProps> = ({
  weekly,
  currency,
  loading = false,
}) => {
  const { t } = useTranslation();

  const hasData = weekly.some((week) => week.current > 0 || week.previous > 0);

  const valueFormatter = (value: number) =>
    formatCashFull(value, currency) ?? "";

  return (
    <Card
      variant="outlined"
      title={t("Spend comparison")}
      subtitle={t("Last 4 weeks vs the 4 before")}
      sx={{ height: "100%", minHeight: 0 }}
    >
      <Box
        sx={{ position: "relative", width: "100%", flex: 1, minHeight: 160 }}
      >
        {loading ? (
          <CustomSkeleton variant="graph" />
        ) : !hasData ? (
          <Typography
            variant="body1"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              padding: "var(--space-8) 0",
            }}
          >
            {t("No spend in this period")}
          </Typography>
        ) : (
          <Box sx={{ position: "absolute", inset: 0 }}>
            <GroupedBarChart
              fill
              legend={{ position: "bottom" }}
              grid={{ horizontal: true }}
              xAxis={[
                {
                  data: weekly.map((week) => t("Wk {{n}}", { n: week.week })),
                  scaleType: "band",
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  valueFormatter: (value) => formatCash(value, currency) ?? "",
                },
              ]}
              series={[
                {
                  id: "previous",
                  label: t("Previous 4 weeks"),
                  data: weekly.map((week) => week.previous),
                  valueFormatter,
                },
                {
                  id: "current",
                  label: t("Last 4 weeks"),
                  data: weekly.map((week) => week.current),
                  valueFormatter,
                },
              ]}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default SpendComparisonChart;
